import React, { useCallback, useMemo } from "react";
import { ThreeEvent } from "@react-three/fiber";
import * as THREE from "three";
import { WallFaceSide, WallType } from "../../../../types/floorplannerTypes";
import {
  clipShapesToWallSide,
  resolveWallFaceAppearance,
  wallHasPerSideFaceMaterials,
} from "../../../../utils/wallFaceSides";
import ExtrudedShapeMesh from "../../scene/ExtrudedShapeMesh";
import Wall3DMaterial from "../../structures/walls/Wall3DMaterial";
import {
  getWallColor,
  restrictExtrudeGeometryToPart,
} from "../../structures/walls/wallGeometry";

type OpeningWallMassPointerHandlers = {
  onPointerDown?: (event: ThreeEvent<PointerEvent>) => void;
  onPointerMove?: (event: ThreeEvent<PointerEvent>) => void;
  onPointerUp?: (event: ThreeEvent<PointerEvent>) => void;
  onPointerCancel?: (event: ThreeEvent<PointerEvent>) => void;
};

type OpeningWallMass3DProps = {
  wall: WallType;
  shape: THREE.Shape;
  depth: number;
  positionZ?: number;
  selected?: boolean;
  wallWidth: number;
  /** Fallback fill when a face has no surface material (matches non-split walls). */
  fallbackColor: THREE.Color;
  configureGeometry?: (
    geometry: THREE.ExtrudeGeometry,
    positionZ: number,
  ) => void;
  userData?: Record<string, unknown>;
} & OpeningWallMassPointerHandlers;

const WALL_FACE_SIDES: WallFaceSide[] = ["left", "right"];

/**
 * Sill / lintel wall mass around openings.
 *
 * Without per-side finishes this is one extrusion using the whole-wall material
 * (same as before). With Side 1 / Side 2 finishes, the footprint is split so
 * each half's vertical faces get that face's wallpaper/paint — otherwise
 * lintels stay gray because Wall3DMaterial no longer falls back to
 * wall.threeD.surfaceRef. Top/bottom lids come from one full-width cap mesh
 * using the shared cap color, so they are not split by side.
 */
const OpeningWallMass3D: React.FC<OpeningWallMass3DProps> = ({
  wall,
  shape,
  depth,
  positionZ = 0,
  selected = false,
  wallWidth,
  fallbackColor,
  configureGeometry,
  userData,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
}) => {
  const usePerSide = wallHasPerSideFaceMaterials(wall);
  const pointerHandlers = {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
  };

  const sideShapes = useMemo(() => {
    if (!usePerSide) return null;
    return WALL_FACE_SIDES.flatMap((side) => {
      const shapes = clipShapesToWallSide([shape], wall, side, wallWidth);
      return shapes.map((sideShape, index) => ({ side, shape: sideShape, index }));
    });
  }, [shape, usePerSide, wall, wallWidth]);

  const configureSides = useCallback(
    (geometry: THREE.ExtrudeGeometry, z: number) => {
      configureGeometry?.(geometry, z);
      restrictExtrudeGeometryToPart(geometry, "sides");
    },
    [configureGeometry],
  );
  const configureCaps = useCallback(
    (geometry: THREE.ExtrudeGeometry, z: number) => {
      configureGeometry?.(geometry, z);
      restrictExtrudeGeometryToPart(geometry, "caps");
    },
    [configureGeometry],
  );

  if (!usePerSide || !sideShapes || sideShapes.length === 0) {
    return (
      <ExtrudedShapeMesh
        shape={shape}
        depth={depth}
        positionZ={positionZ}
        selected={selected}
        receiveShadow
        configureGeometry={configureGeometry}
        userData={userData}
        {...pointerHandlers}
      >
        <Wall3DMaterial wall={wall} color={fallbackColor} />
      </ExtrudedShapeMesh>
    );
  }

  return (
    <>
      <ExtrudedShapeMesh
        shape={shape}
        depth={depth}
        positionZ={positionZ}
        selected={selected}
        receiveShadow
        configureGeometry={configureCaps}
        userData={userData}
        {...pointerHandlers}
      >
        <Wall3DMaterial
          wall={wall}
          color={getWallColor(wall)}
          side={THREE.DoubleSide}
        />
      </ExtrudedShapeMesh>
      {sideShapes.map(({ side, shape: sideShape, index }) => {
        const appearance = resolveWallFaceAppearance(wall, side);
        const color =
          appearance.fillColorIsOverride &&
          appearance.fillColor !== undefined
            ? new THREE.Color(appearance.fillColor)
            : appearance.surfaceRef?.color !== undefined
              ? new THREE.Color(appearance.surfaceRef.color)
              : appearance.fillColor !== undefined
                ? new THREE.Color(appearance.fillColor)
                : fallbackColor;
        return (
          <ExtrudedShapeMesh
            key={`opening-mass-${side}-${index}`}
            shape={sideShape}
            depth={depth}
            positionZ={positionZ}
            selected={selected}
            receiveShadow
            configureGeometry={configureSides}
            userData={{
              ...userData,
              wallFaceSide: side,
            }}
            {...pointerHandlers}
          >
            <Wall3DMaterial
              wall={wall}
              color={color}
              surfaceRef={appearance.surfaceRef}
              side={THREE.DoubleSide}
            />
          </ExtrudedShapeMesh>
        );
      })}
    </>
  );
};

export default OpeningWallMass3D;
